import { useEffect, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import emptyFunction from "fbjs/lib/emptyFunction";
import DatadogTransport from "@analytics/DatadogUtils";
import { IntercomEvents } from "@analytics/enums";
import { getIntercomUserHash } from "api/intercom";
import { getIntercomAppId } from "environment";
import { RootState } from "state/delegate";
import {
  intercomSelectors,
  loginSelectors,
  userSelectors,
  vipConfigsSelectors,
} from "state/selectors";
import { actionCreators } from "state/tree/intercom";
import useMyProfile from "ui/hooks/useMyProfile";
import useVipInfo from "ui/hooks/useVipInfo";
import { vipStatusToIntercomString } from "ui/intercom/useOpenIntercom";

const selector = (state: RootState) => ({
  accountId: userSelectors.getMyAccountId(state),
  isLoggedIn: loginSelectors.isLoggedIn(state),
  isIntercomPaused: intercomSelectors.getIsPaused(state),
  isIntercomBooted: intercomSelectors.getIsBooted(state),
  vipConfigs: vipConfigsSelectors.getVipConfigs(state),
});

export default () => {
  const dispatch = useDispatch();
  const accountIdToUserHashMap = useRef(new Map<string, string>());
  const {
    accountId,
    isLoggedIn,
    isIntercomPaused,
    isIntercomBooted,
    vipConfigs,
  } = useSelector(selector, shallowEqual);
  const { liveStats, basicProfile } = useMyProfile(false);
  const { vipName } = useVipInfo({ vipLevel: liveStats?.vipStatus });
  const vipStatus = vipStatusToIntercomString?.[vipName];
  const fullName = `${basicProfile?.firstName ?? ""} ${
    basicProfile?.lastName ?? ""
  }`.trim();

  useEffect(() => {
    if (
      !getIntercomAppId() ||
      !isLoggedIn ||
      !accountId ||
      isIntercomPaused ||
      liveStats?.vipStatus === null ||
      !vipConfigs?.length
    ) {
      return;
    }

    const boot = (accountId: string, userHash: string) => {
      dispatch(actionCreators.bootIntercom());
      window.Intercom("boot", {
        user_id: accountId,
        user_hash: userHash,
      });

      DatadogTransport.info(IntercomEvents.INTERCOM_BOOT, {
        accountId,
        vipName,
        vipStatus,
        liveStats,
        vipConfigs,
      });
    };

    const shutdown = () => {
      window.Intercom("shutdown");
      dispatch(actionCreators.shutdownIntercom());
    };

    const cachedHash = accountIdToUserHashMap.current.get(accountId);
    if (cachedHash) {
      boot(accountId, cachedHash);

      return () => {
        shutdown();
      };
    }

    let mounted = true;
    getIntercomUserHash()
      .then(({ userHash }) => {
        accountIdToUserHashMap.current.set(accountId, userHash);
        if (mounted) {
          boot(accountId, userHash);
        }
      })
      .catch(emptyFunction);

    return () => {
      mounted = false;
      shutdown();
    };
  }, [
    accountId,
    isLoggedIn,
    isIntercomPaused,
    liveStats,
    vipStatus,
    vipName,
    vipConfigs,
    dispatch,
  ]);

  useEffect(() => {
    if (!isIntercomBooted || isIntercomPaused || !isLoggedIn || !accountId) {
      return;
    }

    window.Intercom("update", {
      user_id: accountId,
      VIP: vipStatus,
      name: fullName,
    });

    DatadogTransport.info(IntercomEvents.INTERCOM_VIP_UPDATE, {
      accountId,
      vipName,
      vipStatus,
      liveStats,
      fullName,
      vipConfigs,
    });
  }, [
    vipStatus,
    isIntercomBooted,
    isIntercomPaused,
    accountId,
    fullName,
    liveStats,
    vipConfigs,
    vipStatus,
    vipName,
    dispatch,
  ]);
};
